import React, { useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Chip,
  Divider,
  IconButton,
  Tooltip,
  Switch,
  FormControlLabel,
} from '@mui/material';
import { Add, Save, Close, UploadFile, Delete } from '@mui/icons-material';

const CreateProjectOrder = () => {
  const [form, setForm] = useState({
    customerName: '',
    customerPhone: '',
    customerEmail: '',
    siteAddress: '',
    projectName: '',
    projectType: 'Residential',
    capacityValue: '',
    capacityUnit: 'kW',
    brand: '',
    installationType: '',
    chargerType: '',
    expectedStart: '',
    expectedEnd: '',
    paymentTerms: 'Advance 30% | On Delivery 60% | On Completion 10%',
    isAMCIncluded: false,
    notes: '',
  });

  const [items, setItems] = useState([
    { id: 1, description: 'Solar Panel Module', qty: 10, unit: 'pcs', price: 12000 },
    { id: 2, description: 'Inverter', qty: 1, unit: 'pcs', price: 45000 },
  ]);

  const [files, setFiles] = useState([]);

  const handleChange = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  const addItem = () => {
    setItems(prev => ([...prev, { id: Date.now(), description: '', qty: 1, unit: 'pcs', price: 0 }]));
  };

  const updateItem = (id, field, value) => {
    setItems(prev => prev.map(i => i.id === id ? { ...i, [field]: value } : i));
  };

  const deleteItem = (id) => setItems(prev => prev.filter(i => i.id !== id));

  const subtotal = items.reduce((sum, i) => sum + (Number(i.qty) * Number(i.price || 0)), 0);
  const tax = +(subtotal * 0.18).toFixed(2);
  const total = +(subtotal + tax).toFixed(2);

  const onUpload = (e) => {
    const list = Array.from(e.target.files || []);
    setFiles(prev => [...prev, ...list.map(f => ({ id: Date.now() + Math.random(), name: f.name, size: f.size }))]);
  };

  const removeFile = (id) => setFiles(prev => prev.filter(f => f.id !== id));

  const handleSubmit = () => {
    if (!form.customerName || !form.projectName || !form.capacityValue) {
      alert('Please fill required fields: Customer Name, Project Name, Capacity');
      return;
    }
    const payload = { ...form, items, totals: { subtotal, tax, total }, attachments: files };
    console.log('Create Project / Order payload:', payload);
    alert('Project / Order created successfully (demo)');
  };

  return (
    <Box sx={{ p: 3, backgroundColor: '#f5f5f5', minHeight: '100vh' }}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 700, color: '#000', mb: 0.5 }}>
          Create Project / Order
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Capture customer, technical, schedule and pricing details for a new green energy project
        </Typography>
      </Box>

      {/* Customer & Site Info */}
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>Customer & Site</Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <TextField fullWidth size="small" label="Customer Name" required value={form.customerName} onChange={(e) => handleChange('customerName', e.target.value)} />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField fullWidth size="small" label="Phone" value={form.customerPhone} onChange={(e) => handleChange('customerPhone', e.target.value)} />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField fullWidth size="small" label="Email" value={form.customerEmail} onChange={(e) => handleChange('customerEmail', e.target.value)} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth size="small" label="Site Address" multiline rows={2} value={form.siteAddress} onChange={(e) => handleChange('siteAddress', e.target.value)} />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Technical Details */}
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>Technical Details</Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <TextField fullWidth size="small" label="Project Name" required value={form.projectName} onChange={(e) => handleChange('projectName', e.target.value)} />
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Project Type</InputLabel>
                <Select value={form.projectType} label="Project Type" onChange={(e) => handleChange('projectType', e.target.value)}>
                  <MenuItem value="Residential">Residential</MenuItem>
                  <MenuItem value="Commercial">Commercial</MenuItem>
                  <MenuItem value="Industrial">Industrial</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={8} md={3}>
              <TextField fullWidth size="small" label="Capacity" required type="number" value={form.capacityValue} onChange={(e) => handleChange('capacityValue', e.target.value)} />
            </Grid>
            <Grid item xs={4} md={1}>
              <FormControl fullWidth size="small">
                <InputLabel>Unit</InputLabel>
                <Select value={form.capacityUnit} label="Unit" onChange={(e) => handleChange('capacityUnit', e.target.value)}>
                  <MenuItem value="kW">kW</MenuItem>
                  <MenuItem value="kWh">kWh</MenuItem>
                  <MenuItem value="MW">MW</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField fullWidth size="small" label="Brand" placeholder="e.g., SunPower / Tesla" value={form.brand} onChange={(e) => handleChange('brand', e.target.value)} />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField fullWidth size="small" label="Installation Type" placeholder="e.g., Rooftop / Ground Mount" value={form.installationType} onChange={(e) => handleChange('installationType', e.target.value)} />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField fullWidth size="small" label="Charger Type (optional)" placeholder="e.g., AC Type 2 / DC CCS2" value={form.chargerType} onChange={(e) => handleChange('chargerType', e.target.value)} />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Schedule */}
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>Schedule</Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <TextField fullWidth size="small" label="Expected Start" type="date" InputLabelProps={{ shrink: true }} value={form.expectedStart} onChange={(e) => handleChange('expectedStart', e.target.value)} />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField fullWidth size="small" label="Expected End" type="date" InputLabelProps={{ shrink: true }} value={form.expectedEnd} onChange={(e) => handleChange('expectedEnd', e.target.value)} />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField fullWidth size="small" label="Payment Terms" value={form.paymentTerms} onChange={(e) => handleChange('paymentTerms', e.target.value)} />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel control={<Switch checked={form.isAMCIncluded} onChange={(e) => handleChange('isAMCIncluded', e.target.checked)} color="success" />} label="Include AMC / Maintenance" />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Bill of Materials / Items */}
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>Materials & Services</Typography>
            <Button variant="outlined" size="small" startIcon={<Add />} onClick={addItem}>Add Item</Button>
          </Box>
          <Grid container spacing={1}>
            {items.map((row) => (
              <Grid key={row.id} item xs={12}>
                <Grid container spacing={1} alignItems="center">
                  <Grid item xs={12} md={5}>
                    <TextField fullWidth size="small" label="Description" value={row.description} onChange={(e) => updateItem(row.id, 'description', e.target.value)} />
                  </Grid>
                  <Grid item xs={6} md={2}>
                    <TextField fullWidth size="small" label="Qty" type="number" value={row.qty} onChange={(e) => updateItem(row.id, 'qty', e.target.value)} />
                  </Grid>
                  <Grid item xs={6} md={2}>
                    <TextField fullWidth size="small" label="Unit" value={row.unit} onChange={(e) => updateItem(row.id, 'unit', e.target.value)} />
                  </Grid>
                  <Grid item xs={8} md={2}>
                    <TextField fullWidth size="small" label="Unit Price" type="number" value={row.price} onChange={(e) => updateItem(row.id, 'price', e.target.value)} />
                  </Grid>
                  <Grid item xs={4} md={1}>
                    <Tooltip title="Remove">
                      <IconButton onClick={() => deleteItem(row.id)} size="small" color="error"><Delete fontSize="small" /></IconButton>
                    </Tooltip>
                  </Grid>
                </Grid>
              </Grid>
            ))}
          </Grid>
          <Divider sx={{ my: 2 }} />
          <Box display="flex" gap={1} flexWrap="wrap">
            <Chip label={`Subtotal: ₹${subtotal.toLocaleString('en-IN')}`} color="default" />
            <Chip label={`Tax (18%): ₹${tax.toLocaleString('en-IN')}`} color="warning" />
            <Chip label={`Total: ₹${total.toLocaleString('en-IN')}`} color="primary" />
          </Box>
        </CardContent>
      </Card>

      {/* Attachments */}
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>Attachments</Typography>
          <Button variant="outlined" startIcon={<UploadFile />} component="label" size="small" sx={{ mb: 1 }}>
            Upload Files
            <input hidden multiple type="file" onChange={onUpload} />
          </Button>
          <Box display="flex" gap={1} flexWrap="wrap">
            {files.map(f => (
              <Chip key={f.id} label={`${f.name}`} onDelete={() => removeFile(f.id)} />
            ))}
          </Box>
        </CardContent>
      </Card>

      {/* Notes & Actions */}
      <Card>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={9}>
              <TextField fullWidth size="small" label="Notes" multiline rows={2} value={form.notes} onChange={(e) => handleChange('notes', e.target.value)} />
            </Grid>
            <Grid item xs={12} md={3}>
              <Box display="flex" gap={1} justifyContent={{ xs: 'flex-start', md: 'flex-end' }}>
                <Button variant="outlined" startIcon={<Close />}>Cancel</Button>
                <Button variant="contained" startIcon={<Save />} onClick={handleSubmit} sx={{ backgroundColor: '#536DFE', '&:hover': { backgroundColor: '#3F51B5' } }}>Create</Button>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </Box>
  );
};

export default CreateProjectOrder;


